import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, throwError } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { YesNoModalDialogComponent } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.component';
import { YesNoModalDialogModel } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.model';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { TimesheetAndExpensesInstanceToken, TimesheetAndExpensesInterface } from './timesheet-and-expenses.interface';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { WeeklyWorkTimeInstanceToken, WeeklyWorkTimeInterface } from '../../interfaces/weekly-work-time.interface';

@Injectable({
    providedIn: 'root',
})
export class StartWorkService {
    private warningBreakTimeHoursAcknowledged = false;
    public constructor(
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(WeeklyWorkTimeInstanceToken) private readonly weeklyWorkTimeService: WeeklyWorkTimeInterface,
    ) {}

    /**
     * This method saves start of work updated by user from frequent activities screen or from details screen
     * It will also validate the SOW date and time and throw warning popup if it is invalid
     *
     * @param startOfWork Start of work as moment object
     *
     * @returns Observable of nothing once save process has completed
     */
    public setStartOfWork(startOfWork: moment.Moment): Observable<void> {
        return this.timesheetAndExpensesService.selectSubmittedTimesheet$().pipe(
            take(1),
            mergeMap((submittedTimesheet) => {
                const endOfWork = submittedTimesheet.getEndOfWork();
                const dateTimeFormat = this.clientManager.getDateTimeFormat();

                // end-of-work should always be before a new start-of-work
                const showWarningEowAfterSow = endOfWork.isAfter(startOfWork);

                if (showWarningEowAfterSow) {
                    this.log.debug(
                        `StartWorkService: setStartOfWork: Displaying warning about EoW (${endOfWork.toString()}) being after SoW (${startOfWork.toString()})`,
                    );

                    const errorText = <string>this.translate.instant('SOW_BEFORE_EOW_TEXT', {
                        endOfWork: endOfWork.format(dateTimeFormat),
                        startOfWork: startOfWork.format(dateTimeFormat),
                    });

                    const warningEowAfterSowModalInstance = this.modalService.show(YesNoModalDialogComponent, {
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            model: new YesNoModalDialogModel(
                                <string>this.translate.instant('SOW_BEFORE_EOW_TITLE'),
                                errorText,
                                <string>this.translate.instant('SOW_BEFORE_EOW_OK_LABEL'),
                            ),
                        },
                        ariaLabelledBy: 'yes-no-modal-title',
                    });

                    warningEowAfterSowModalInstance.content?.modalResult.subscribe(() => {
                        this.log.debug(
                            `StartWorkService: setStartOfWork: Warning about EoW after SoW was acknowledged by the user`,
                        );
                    });

                    return throwError(
                        () => new Error(`${ConfigConstants.TravelStatusErrors.INVALID_START_OF_WORK_ERROR}`),
                    );
                }

                // check for 11h break between EoW and SoW
                const breakTimeHours = this.configService.getEowSowBreakHours();

                // getEndOfWork() falls back to "now" when there is no previous timesheet at all (SHP-9004),
                // which must not be mistaken for an actual recent end-of-work when checking the break time.
                const hasPreviousEndOfWork = submittedTimesheet.getLastEntryDateTime().isValid();

                // if a break time is configured, and we have a previous timesheet, we can check
                const showWarningBreakTimeHours =
                    breakTimeHours > 0 &&
                    hasPreviousEndOfWork &&
                    startOfWork.diff(endOfWork, 'minutes') < breakTimeHours * ConfigConstants.timeMultiplier;

                if (showWarningBreakTimeHours && !this.warningBreakTimeHoursAcknowledged) {
                    this.log.debug(
                        `StartWorkService: setStartOfWork: Displaying warning about less than 11h between EoW (${endOfWork.toString()}) and SoW (${startOfWork.toString()})`,
                    );
                    const breakWarningText = <string>this.translate.instant('EOW_SOW_BREAK_WARNING_TEXT', {
                        endOfWork: endOfWork.format(dateTimeFormat),
                        startOfWork: startOfWork.format(dateTimeFormat),
                        breakHours: breakTimeHours,
                    });

                    const warningBreakTimeHoursModalInstance = this.modalService.show(YesNoModalDialogComponent, {
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            model: new YesNoModalDialogModel(
                                <string>this.translate.instant('EOW_SOW_BREAK_WARNING_TITLE'),
                                breakWarningText,
                                <string>this.translate.instant('EOW_SOW_BREAK_WARNING_OK_LABEL'),
                            ),
                        },
                        ariaLabelledBy: 'yes-no-modal-title',
                    });

                    warningBreakTimeHoursModalInstance.content?.modalResult.subscribe(() => {
                        this.log.debug(
                            `StartWorkService: setStartOfWork: Warning about 11h break was acknowledged by the user`,
                        );
                        this.warningBreakTimeHoursAcknowledged = true;
                    });

                    return throwError(
                        () => new Error(`${ConfigConstants.TravelStatusErrors.START_OF_WORK_BREAK_WARNING}`),
                    );
                }

                return this.timesheetAndExpensesService.saveStartOfWork(startOfWork);
            }),
            tap(() => {
                this.log.debug(`StartWorkService: setStartOfWork: set startOfWork: ${startOfWork}`);
                this.warningBreakTimeHoursAcknowledged = false;
            }),
            mergeMap(() => this.weeklyWorkTimeService.syncWeeklyWorkTime()),
            map(() => undefined),
        );
    }
}
